/*
 * Free gift with purchase (Sep 29 2026, subscription rule Oct 5 2026).
 *
 * Keeps a $0 gift line (the free GROW Scalp Mist) in the cart while the cart
 * holds enough qualifying products, and takes it out again when it does not.
 * Settings come from window.AkashaGWP, printed by
 * snippets/gift-with-purchase.liquid (mode 'head'), which also documents the
 * counting rule. Keep the two in step.
 *
 * Subscription carts (Oct 5 2026): from Oct 3 every subscription checkout
 * that carried the gift as a $0-priced line failed at payment ("There was
 * an issue processing your payment"); the same carts without it paid
 * (#139182). The gift product is now priced at list and brought to $0 by
 * the automatic discount "Free GROW Scalp Mist (gift with 3+ products)".
 * Whether subscription carts get the gift line is a switch: the tag
 * "gwp-subscriptions" on the gift product (printed as
 * AkashaGWP.subscriptionCarts by the snippet). Without the tag a cart that
 * holds any selling-plan line never carries the gift, so the switch can be
 * turned off from the product page in seconds if checkout ever fails again.
 *
 * How it hooks in: this theme has several independent add-to-cart paths (the
 * PDP buy box, the sticky bar, the bundle builder, the cart drawer's own
 * buttons, the cart page, apps). Rather than patch each one, every Ajax cart
 * mutation (cart/add, cart/change, cart/update, cart/clear) made through
 * fetch is held until the gift has been synced. If the caller asked Shopify
 * to render sections in the same response, those sections are re-rendered
 * after the sync so the drawer it paints already shows the gift. XHR callers
 * and full page loads are covered by a sync afterwards plus a quiet drawer
 * refresh.
 */
(function () {
  'use strict';

  var C = window.AkashaGWP;
  if (!C || !C.giftVariantId || window.__akashaGWP) return;
  window.__akashaGWP = true;

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var GIFT = Number(C.giftVariantId);
  var THRESHOLD = Number(C.threshold) || 3;
  var UNITS = C.units || {};
  var SKIP_TYPES = (C.skipTypes || []).map(lower);
  var SKIP_VENDORS = (C.skipVendors || []).map(lower);
  var SUBS = !!C.subscriptionCarts; // gift allowed in subscription carts?
  var MUTATION = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  var nativeFetch = window.fetch.bind(window);
  var broken = false; // set if adding the gift fails, so we never loop

  function lower(s) { return String(s || '').toLowerCase(); }
  function isGift(item) { return Number(item.variant_id) === GIFT; }

  // True when any line in the cart is on a selling plan (a subscription).
  function hasSubscription(cart) {
    return (cart.items || []).some(function (it) { return !!it.selling_plan_allocation; });
  }

  function qualifyingUnits(cart) {
    return (cart.items || []).reduce(function (n, it) {
      if (isGift(it) || it.gift_card) return n;
      if (SKIP_TYPES.indexOf(lower(it.product_type)) > -1) return n;
      if (SKIP_VENDORS.indexOf(lower(it.vendor)) > -1) return n;
      var price = it.original_price != null ? it.original_price : it.price;
      if (!(price > 0)) return n;
      return n + it.quantity * (Number(UNITS[it.product_id]) || 1);
    }, 0);
  }

  function getCart() {
    return nativeFetch(ROOT + 'cart.js', { cache: 'no-store', credentials: 'same-origin' })
      .then(function (r) { return r.json(); });
  }

  function post(path, body) {
    return nativeFetch(ROOT + path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) throw new Error('gift sync ' + path + ' ' + r.status);
      return r.json();
    });
  }

  // Resolves true when the cart was changed.
  function syncOnce() {
    return getCart().then(function (cart) {
      // Subscription carts carry the gift only when the switch is on (see header).
      var want = C.enabled && !broken && (SUBS || !hasSubscription(cart)) && qualifyingUnits(cart) >= THRESHOLD ? 1 : 0;
      var lines = (cart.items || []).filter(isGift);
      var have = lines.reduce(function (n, it) { return n + it.quantity; }, 0);
      if (have === want && lines.length <= 1) return false;

      if (!lines.length) {
        return post('cart/add.js', {
          items: [{ id: GIFT, quantity: 1, properties: { _promo: 'Free gift with 3+ products' } }]
        }).then(function () { return true; }, function (e) {
          broken = true;
          console.warn('[gift]', e);
          return false;
        });
      }
      var updates = {};
      lines.forEach(function (it, i) { updates[it.key] = i === 0 ? want : 0; });
      return post('cart/update.js', { updates: updates }).then(function () { return true; });
    });
  }

  // Serialise syncs: one in flight, at most one queued behind it.
  var inflight = null;
  var queued = false;
  function sync() {
    if (inflight) { queued = true; return inflight; }
    inflight = syncOnce().catch(function (e) { console.warn('[gift]', e); return false; })
      .then(function (changed) {
        inflight = null;
        if (queued) {
          queued = false;
          return sync().then(function (again) { return changed || again; });
        }
        return changed;
      });
    return inflight;
  }

  // ---- UI refresh helpers (used when no caller will repaint for us) ----

  function drawerSectionId() {
    var el = document.querySelector('.shopify-section[id*="cart-drawer"]');
    return el ? el.id.replace('shopify-section-', '') : null;
  }

  function updateCount(cart) {
    document.querySelectorAll('cart-count').forEach(function (el) {
      el.textContent = cart.item_count;
      el.setAttribute('aria-label', cart.item_count + ' items');
      if (cart.item_count > 0) el.removeAttribute('hidden'); else el.setAttribute('hidden', '');
    });
  }

  function refreshDrawerQuietly() {
    var id = drawerSectionId();
    var live = document.querySelector('#CartDrawer .cart-drawer-inner');
    var jobs = [getCart().then(updateCount)];
    if (id && live) {
      jobs.push(nativeFetch(location.pathname + '?sections=' + encodeURIComponent(id), { cache: 'no-store' })
        .then(function (r) { return r.json(); })
        .then(function (d) {
          var html = d && d[id];
          if (!html) return;
          var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('.cart-drawer-inner');
          var now = document.querySelector('#CartDrawer .cart-drawer-inner');
          if (fresh && now) now.innerHTML = fresh.innerHTML;
          if (typeof window.updateShippingBar === 'function') { try { window.updateShippingBar(); } catch (e) {} }
        }));
    }
    return Promise.all(jobs).catch(function () {});
  }

  // After a change, check the painted drawer against the cart a moment later
  // and repaint only if some caller left it stale.
  var checkTimer = null;
  function checkDrawerSoon() {
    clearTimeout(checkTimer);
    checkTimer = setTimeout(function () {
      getCart().then(function (cart) {
        var hasGift = (cart.items || []).some(isGift);
        var drawer = document.getElementById('CartDrawer');
        if (!drawer) return;
        var painted = !!drawer.querySelector('[data-gwp-line]');
        if (painted !== hasGift) refreshDrawerQuietly();
      }).catch(function () {});
    }, 900);
  }

  // Re-render any sections the caller asked for, and refresh cart fields,
  // so the response it receives describes the cart after the gift sync.
  function freshen(res) {
    return res.clone().json().then(function (data) {
      if (!data || typeof data !== 'object') return res;
      var jobs = [];
      var ids = data.sections && typeof data.sections === 'object' ? Object.keys(data.sections) : [];
      if (ids.length) {
        jobs.push(nativeFetch(location.pathname + '?sections=' + ids.map(encodeURIComponent).join(','), { cache: 'no-store' })
          .then(function (r) { return r.json(); })
          .then(function (fresh) {
            ids.forEach(function (id) { if (fresh && fresh[id] != null) data.sections[id] = fresh[id]; });
          }));
      }
      if (Array.isArray(data.items) && 'item_count' in data) {
        jobs.push(getCart().then(function (cart) {
          Object.keys(cart).forEach(function (k) { data[k] = cart[k]; });
        }));
      }
      if (!jobs.length) return res;
      return Promise.all(jobs).then(function () {
        var headers = new Headers(res.headers);
        headers.delete('content-length');
        headers.delete('content-encoding');
        headers.set('content-type', 'application/json; charset=utf-8');
        return new Response(JSON.stringify(data), { status: res.status, statusText: res.statusText, headers: headers });
      });
    }).catch(function () { return res; });
  }

  function urlOf(input) {
    if (typeof input === 'string') return input;
    if (input && typeof input.url === 'string') return input.url;
    return String(input || '');
  }

  // ---- fetch hook ----
  window.fetch = function (input, init) {
    var p = nativeFetch(input, init);
    var url = urlOf(input).split('#')[0];
    var method = String((init && init.method) || (input && input.method) || 'GET').toUpperCase();
    if (method !== 'POST' || !MUTATION.test(url)) return p;
    return p.then(function (res) {
      if (!res.ok) return res;
      return sync().then(function (changed) {
        if (!changed) return res;
        checkDrawerSoon();
        return freshen(res);
      }, function () { return res; });
    });
  };

  // ---- XHR hook (older apps) ----
  var xOpen = XMLHttpRequest.prototype.open;
  var xSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__gwp = String(method || '').toUpperCase() === 'POST' && MUTATION.test(String(url || ''));
    return xOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function () {
    if (this.__gwp) {
      this.addEventListener('loadend', function () {
        sync().then(function (changed) { if (changed) refreshDrawerQuietly(); });
      });
    }
    return xSend.apply(this, arguments);
  };

  // ---- page load: covers form posts, cart links and carts from an earlier visit ----
  function onLoad() {
    sync().then(function (changed) {
      // The cart page is rendered on the server, so if the gift changed
      // underneath it, reload once. The flag stops any chance of a loop.
      var key = 'gwp-reloaded';
      var already = false;
      try { already = sessionStorage.getItem(key) === '1'; } catch (e) {}
      if (!changed) {
        if (already) { try { sessionStorage.removeItem(key); } catch (e) {} }
        return;
      }
      if (document.querySelector('main-cart') && !already) {
        try { sessionStorage.setItem(key, '1'); } catch (e) {}
        location.reload();
        return;
      }
      refreshDrawerQuietly();
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', onLoad);
  else onLoad();

  window.AkashaGWP.sync = sync;
  window.AkashaGWP.qualifyingUnits = qualifyingUnits;
  window.AkashaGWP.hasSubscription = hasSubscription;
})();
